import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const SmartRoutingDisplay = ({ paymentData, routingInfo, status }) => {
  const [progress, setProgress] = useState(0);
  const [currentConfirmations, setCurrentConfirmations] = useState(0);

  useEffect(() => {
    if (status === 'processing') {
      const interval = setInterval(() => {
        setProgress(prev => Math.min(prev + 10, 100));
      }, 150);
      return () => clearInterval(interval);
    } else if (status === 'confirming') {
      const interval = setInterval(() => {
        setCurrentConfirmations(prev => {
          if (prev < routingInfo?.confirmations) {
            return prev + 1;
          }
          return prev;
        });
      }, 800);
      return () => clearInterval(interval);
    }
  }, [status, routingInfo]);

  const getRailIcon = (rail) => {
    const icons = {
      'lightning': 'Zap',
      'on-chain': 'Link',
      'layer-2': 'Layers',
      'polygon': 'Hexagon'
    };
    return icons?.[rail] || 'Activity';
  };

  const getRailColor = (rail) => {
    const colors = {
      'lightning': 'text-warning',
      'on-chain': 'text-primary',
      'layer-2': 'text-success',
      'polygon': 'text-purple-500'
    };
    return colors?.[rail] || 'text-primary';
  };

  const cryptoNames = {
    'btc': 'Bitcoin',
    'eth': 'Ethereum',
    'usdc': 'USD Coin',
    'usdt': 'Tether'
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center pb-4 border-b border-border">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
          <Icon name="Zap" size={32} className="text-primary" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-1">
          {status === 'processing' ? 'Processing Payment' : 'Confirming Transaction'}
        </h3>
        <p className="text-sm text-muted-foreground">
          {status === 'processing' ?'Calculating optimal payment route...' :'Waiting for blockchain confirmations...'}
        </p>
      </div>

      {/* Payment Summary */}
      <div className="p-4 rounded-lg bg-muted/20 border border-border">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-muted-foreground">Payment Amount</span>
          <div className="text-right">
            <div className="text-lg font-bold text-foreground">
              {paymentData?.amount} {cryptoNames?.[paymentData?.selectedCrypto]?.split(' ')?.[0]}
            </div>
            <div className="text-xs text-muted-foreground">
              ≈ ${parseFloat(paymentData?.usdAmount)?.toFixed(2)} USD
            </div>
          </div>
        </div>
      </div>

      {/* Smart Routing Info */}
      {status === 'confirming' && routingInfo && (
        <div className="space-y-4">
          {/* Selected Rail */}
          <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center`}>
                <Icon name={getRailIcon(routingInfo?.selectedRail)} size={20} className={getRailColor(routingInfo?.selectedRail)} />
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium text-foreground">Selected Payment Rail</div>
                <div className="text-xs text-muted-foreground capitalize">
                  {routingInfo?.selectedRail?.replace('-', ' ')}
                </div>
              </div>
              <div className="px-2 py-1 rounded bg-success/10 text-success text-xs font-medium">
                Optimal
              </div>
            </div>
          </div>

          {/* Fee Breakdown */}
          <div className="p-4 rounded-lg bg-muted/10 border border-border space-y-3">
            <h4 className="text-sm font-semibold text-foreground mb-3">Fee Breakdown</h4>
            
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Network Fee</span>
              <span className="font-medium text-foreground">${routingInfo?.networkFee?.toFixed(2)}</span>
            </div>
            
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Processing Fee (1.5%)</span>
              <span className="font-medium text-foreground">${routingInfo?.processingFee?.toFixed(2)}</span>
            </div>
            
            <div className="pt-2 border-t border-border flex items-center justify-between">
              <span className="text-sm font-semibold text-foreground">Total Fees</span>
              <span className="text-lg font-bold text-foreground">${routingInfo?.totalFee?.toFixed(2)}</span>
            </div>
          </div>

          {/* Confirmation Progress */}
          <div className="p-4 rounded-lg bg-muted/10 border border-border">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-foreground">Blockchain Confirmations</span>
              <span className="text-sm font-bold text-primary">
                {currentConfirmations} / {routingInfo?.confirmations}
              </span>
            </div>
            
            <div className="w-full bg-muted rounded-full h-2 mb-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-500"
                style={{ width: `${(currentConfirmations / routingInfo?.confirmations) * 100}%` }}
              />
            </div>
            
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Estimated time: {routingInfo?.estimatedTime}</span>
              <span>{Math.round((currentConfirmations / routingInfo?.confirmations) * 100)}%</span>
            </div>
          </div>

          {/* Rate Lock Info */}
          <div className="flex items-start gap-2 p-3 bg-warning/10 border border-warning/20 rounded-lg">
            <Icon name="Lock" size={16} className="text-warning mt-0.5" />
            <div className="flex-1">
              <p className="text-xs font-medium text-foreground">Rate Locked</p>
              <p className="text-xs text-muted-foreground">
                Exchange rate guaranteed until {new Date(routingInfo?.rateLockedUntil)?.toLocaleTimeString()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Processing Animation */}
      {status === 'processing' && (
        <div className="space-y-3">
          <div className="w-full bg-muted rounded-full h-2">
            <div 
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <div className="animate-spin">
              <Icon name="Loader" size={16} />
            </div>
            <span>Analyzing network conditions...</span>
          </div>
        </div>
      )}

      {/* Transaction Details */}
      <div className="pt-4 border-t border-border space-y-2">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Transaction ID</span>
          <span className="font-mono text-foreground">0x7a8f...9d2e</span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Wallet Address</span>
          <span className="font-mono text-foreground">
            {paymentData?.walletAddress?.slice(0, 8)}...{paymentData?.walletAddress?.slice(-6)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default SmartRoutingDisplay;